import type { FacilityNode, RoleCard, RoleGrant } from "@arc/contracts";

/**
 * The bucket for roles without a dash (the legacy's hardcoded "ohne Gruppe") carries `label: null`, not a string.
 *
 * This module is plain logic — no hook, no translator — so a label baked in here would be English forever. Making
 * the absence a `null` rather than a word pushes the wording to the render site and makes the type system ask every
 * consumer for it, instead of letting one quietly print the English through.
 */
export const NO_GROUP_LABEL = null;

export interface RoleGroup {
  key: string | null;
  label: string | null;
  roles: RoleCard[];
}

const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Roles grouped by their server-derived prefix: prefix groups in natural order, roles by short name, "No group" LAST
 *  (an ADR-030 improvement — the legacy let it land first by appearance order). Pure. */
export function groupRoles(cards: readonly RoleCard[]): RoleGroup[] {
  const byPrefix = new Map<string | null, RoleCard[]>();
  for (const c of cards) {
    const list = byPrefix.get(c.prefix) ?? [];
    list.push(c);
    byPrefix.set(c.prefix, list);
  }
  const groups: RoleGroup[] = [...byPrefix.entries()]
    .filter((e): e is [string, RoleCard[]] => e[0] !== null)
    .map(([k, roles]) => ({ key: k, label: k, roles: [...roles].sort((a, b) => natural(a.shortName, b.shortName)) }))
    .sort((a, b) => natural(a.label, b.label));
  const none = byPrefix.get(null);
  if (none) groups.push({ key: null, label: NO_GROUP_LABEL, roles: [...none].sort((a, b) => natural(a.shortName, b.shortName)) });
  return groups;
}

/** The legacy "Grund-Ordner" line: the facility's ancestor chain + itself, or why it cannot be shown. */
export function rootFolderPath(flat: readonly FacilityNode[], id: number | null): { kind: "none" } | { kind: "missing"; id: number } | { kind: "path"; names: string[] } {
  if (id === null) return { kind: "none" };
  const byId = new Map(flat.map((f) => [f.id, f]));
  const node = byId.get(id);
  if (!node) return { kind: "missing", id };
  const names: string[] = [node.name];
  let pid = node.parentId;
  while (pid !== null) {
    const p = byId.get(pid);
    if (!p) break;
    names.unshift(p.name);
    pid = p.parentId;
  }
  return { kind: "path", names };
}

/** Split a role's grants into the ones the caller's tree can show, and the counts of those it cannot (facilities the
 *  caller does not see, and archived ones — the tree excludes them). */
export function partitionGrants(grants: readonly RoleGrant[], visibleIds: ReadonlySet<number>, archivedIds: ReadonlySet<number>): { visible: Map<number, RoleGrant>; outsideCount: number; archivedCount: number } {
  const visible = new Map<number, RoleGrant>();
  let outsideCount = 0;
  let archivedCount = 0;
  for (const g of grants) {
    if (archivedIds.has(g.facilityId)) archivedCount += 1;
    else if (visibleIds.has(g.facilityId)) visible.set(g.facilityId, g);
    else outsideCount += 1;
  }
  return { visible, outsideCount, archivedCount };
}
